import type { ElementType, ReactNode } from "react";

/**
 * PostHog's built-in session-replay recorder honors these class names out of
 * the box (rrweb `blockClass` / `maskTextClass` defaults) — no custom
 * selector config required, so they keep working even if a container is
 * rendered before the config from analytics/session-replay.ts had a chance to
 * apply.
 */
export const ANALYTICS_BLOCK_CLASS = "ph-no-capture";
export const ANALYTICS_MASK_CLASS = "ph-mask";

/**
 * Belt-and-braces marker also referenced by session-replay.ts's
 * `blockSelector`, for places a class name is inconvenient to add (e.g. a
 * component that merges its own `className` prop unpredictably).
 */
export const ANALYTICS_PRIVATE_ATTRIBUTE = "data-analytics-private";

type AnalyticsPrivateProps = {
  children: ReactNode;
  /** Renders as a `<div>` by default; pass e.g. "span" for inline content. */
  as?: ElementType;
  className?: string;
  /**
   * "block" (default) fully replaces the container with a placeholder in
   * session replay — use for anything that renders user-created or
   * AI-generated text (task/goal titles, reflections, AI recommendations,
   * profile fields). "mask" only redacts text nodes and is for cases where
   * the surrounding layout must remain visible in replay.
   */
  mode?: "block" | "mask";
  "aria-labelledby"?: string;
  "data-testid"?: string;
};

/**
 * Wraps content that must never be visible in a PostHog session recording.
 * Rendering is unaffected outside of session replay — this only adds a class
 * name and a data attribute that the replay recorder recognizes.
 */
export function AnalyticsPrivate({
  children,
  as: Component = "div",
  className,
  mode = "block",
  ...elementProps
}: AnalyticsPrivateProps) {
  const modeClass =
    mode === "block" ? ANALYTICS_BLOCK_CLASS : ANALYTICS_MASK_CLASS;
  const combinedClassName = className ? `${modeClass} ${className}` : modeClass;
  return (
    <Component
      className={combinedClassName}
      {...{ [ANALYTICS_PRIVATE_ATTRIBUTE]: "true" }}
      {...elementProps}
    >
      {children}
    </Component>
  );
}
